//client/src/pages/admin/ManageLinks.js
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import adminService from '../../services/adminService.js';
import Button from '../../components/ui/Button.js';
import Input from '../../components/ui/Input.js';
import Modal from '../../components/ui/Modal.js';

export default function ManageLinks() {
  const [links, setLinks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', url: '', shortcode: '', description: '', isprimarycta: false });

  useEffect(() => {
    adminService.getLinks().then((d) => setLinks(d.links));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editing) {
      await adminService.updateLink(editing, form);
    } else {
      await adminService.createLink(form);
    }
    setShowForm(false);
    setEditing(null);
    setForm({ name: '', url: '', shortcode: '', description: '', isprimarycta: false });
    adminService.getLinks().then((d) => setLinks(d.links));
  };

  const handleEdit = (link) => {
    setForm({
      name: link.name || '',
      url: link.url || '',
      shortcode: link.shortcode || '',
      description: link.description || '',
      isprimarycta: link.isprimarycta || false,
      isactive: link.isactive !== false,
    });
    setEditing(link.linkid);
    setShowForm(true);
  };

  const handleAddNew = () => {
    setEditing(null);
    setForm({ name: '', url: '', shortcode: '', description: '', isprimarycta: false });
    setShowForm(true);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Dashboard Back Link */}
      <div className="mb-5">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Dashboard
        </Link>
      </div>

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl text-gold">Admin Links</h1>
          <p className="text-royal-200 text-sm mt-1">
            Manage CTA links. Mark one as "Primary CTA" to display it on eCard view pages.
          </p>
        </div>
        <Button onClick={handleAddNew}>Add Link</Button>
      </div>

      {/* Primary CTA Info Box */}
      {links.some(l => l.isprimarycta) && (
        <div className="bg-gold/10 border border-gold/30 rounded-lg p-4 mb-6">
          <p className="text-gold text-sm">
            <span className="font-semibold">Primary CTA:</span>{' '}
            {links.find(l => l.isprimarycta)?.name} → {links.find(l => l.isprimarycta)?.url}
          </p>
          <p className="text-royal-200 text-xs mt-1">
            This link appears as the CTA button on all eCard view pages.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {links.map((l) => (
          <div
            key={l.linkid}
            className={`bg-royal-800/40 border rounded-lg p-4 flex justify-between items-start ${
              l.isprimarycta ? 'border-gold/50' : 'border-royal-600'
            }`}
          >
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <p className="text-white font-semibold">{l.name}</p>
                {l.isprimarycta && (
                  <span className="px-2 py-0.5 bg-gold/20 text-gold text-xs rounded-full font-medium">
                    Primary CTA
                  </span>
                )}
                {!l.isactive && (
                  <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                    Inactive
                  </span>
                )}
              </div>
              <p className="text-royal-200 text-sm mt-1">{l.url}</p>
              {l.description && (
                <p className="text-royal-200 text-xs mt-1">{l.description}</p>
              )}
              <p className="text-royal-200 text-xs mt-2">
                Shortcode: {l.shortcode || '—'} · {l.clickcount || 0} clicks
              </p>
            </div>
            <button
              className="text-gold text-sm hover:text-gold-light transition-colors"
              onClick={() => handleEdit(l)}
            >
              Edit
            </button>
          </div>
        ))}

        {links.length === 0 && (
          <div className="text-center py-12 text-royal-200">
            <p>No links yet. Add your first link to get started.</p>
          </div>
        )}
      </div>

      <Modal isOpen={showForm} onClose={() => setShowForm(false)} title={editing ? 'Edit Link' : 'New Link'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g., Create Your Own eCard"
            required
          />
          <Input
            label="URL"
            value={form.url}
            onChange={(e) => setForm({ ...form, url: e.target.value })}
            placeholder="e.g., https://yoursite.com/moonbeams"
            required
          />
          <Input
            label="Short Code (optional)"
            value={form.shortcode}
            onChange={(e) => setForm({ ...form, shortcode: e.target.value })}
            placeholder="e.g., create-card"
          />
          <Input
            label="Description (optional)"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Internal note about this link"
          />

          {/* Primary CTA Toggle */}
          <div className="bg-royal-800/40 border border-royal-600 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.isprimarycta || false}
                onChange={(e) => setForm({ ...form, isprimarycta: e.target.checked })}
                className="mt-1 w-5 h-5 rounded border-royal-600 bg-royal-800 text-gold focus:ring-gold focus:ring-offset-0"
              />
              <div>
                <span className="text-white font-medium">Primary CTA</span>
                <p className="text-royal-200 text-sm mt-0.5">
                  Display this link as the main call-to-action button on eCard view pages.
                  Only one link can be primary at a time.
                </p>
              </div>
            </label>
          </div>

          {/* Active Toggle (only when editing) */}
          {editing && (
            <div className="bg-royal-800/40 border border-royal-600 rounded-lg p-4">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isactive !== false}
                  onChange={(e) => setForm({ ...form, isactive: e.target.checked })}
                  className="mt-1 w-5 h-5 rounded border-royal-600 bg-royal-800 text-gold focus:ring-gold focus:ring-offset-0"
                />
                <div>
                  <span className="text-white font-medium">Active</span>
                  <p className="text-royal-200 text-sm mt-0.5">
                    Inactive links won't be displayed or tracked.
                  </p>
                </div>
              </label>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button type="submit" className="flex-1">
              {editing ? 'Update Link' : 'Create Link'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowForm(false)}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
